import React, { useEffect, useState, type FC } from 'react';
import { useParams } from 'react-router';
import { NavLink } from 'react-router-dom';

import axios, { type AxiosResponse } from 'axios';

import { Breadcrumb } from '@shared/components/bootstrap';

import { PageHeader } from '@admin/components/helpers/page_header';
import { useAxiosRequest } from '@admin/hooks/use_axios_request';
import { useNavigate } from '@admin/hooks/use_navigate';
import { useTitle } from '@admin/hooks/use_title';

import { LocationForm, type LocationFormData } from './form';

type Warehouse = {
  id: number;
  name: string;
};

type Location = {
  id: number;
  name?: string;
  value: string;
  located?: {
    object?: {
      id: number;
      name: string;
    };
  };
} & LocationFormData;

const useWarehouseRequest = (warehouseID: number | string) => {
  const [request, setRequest] = useState<Promise<AxiosResponse<Warehouse>> | undefined>();
  useEffect(() => {
    setRequest(axios.get<Warehouse>(`/warehouses/${warehouseID}.json`));
  }, [warehouseID]);
  return useAxiosRequest(request);
};

const useLocationCreateRequest = (warehouseID: number | string) => {
  const [request, setRequest] = useState<Promise<AxiosResponse<Location>> | undefined>();

  const save = (data: LocationFormData) => {
    const request = axios.post<Location>(`/warehouses/${warehouseID}/locations.json`, data);
    setRequest(request);
    return request;
  };

  return [save, useAxiosRequest(request)] as const;
};

export const LocationNew: FC = () => {
  useTitle('Location');
  const { warehouseID } = useParams<{ warehouseID: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | undefined>();
  const { response } = useWarehouseRequest(warehouseID);
  const [save, { loading }] = useLocationCreateRequest(warehouseID);
  const warehouse = response?.data;

  const onSave = async (data: LocationFormData) => {
    if (loading) return;
    try {
      setError(undefined);
      const response = await save(data);
      navigate(`/locations/${response.data.id}`);
    } catch (error) {
      if (axios.isAxiosError(error)) setError(error.response?.data?.message);
      else setError('An unknown error occurred');
    }
  };

  return (
    <>
      <PageHeader>
        <Breadcrumb>
          <Breadcrumb.Item>
            <NavLink to="/warehouses">Warehouses</NavLink>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <NavLink to={`/warehouses/${warehouseID}`}>{warehouse?.name ?? '-'}</NavLink>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <NavLink to={`/warehouses/${warehouseID}/locations`}>Locations</NavLink>
          </Breadcrumb.Item>
          <Breadcrumb.Item active>New</Breadcrumb.Item>
        </Breadcrumb>
      </PageHeader>
      <LocationForm error={error} loading={loading} onSave={onSave} />
    </>
  );
};
